import React, { useEffect, useMemo, useState } from 'react';
import {
  Line, LineChart, ResponsiveContainer, Tooltip, XAxis, YAxis, CartesianGrid,
} from 'recharts';
import { formatDateTick } from './format';
import apiClient from '../../utils/api';

// The index over time — and, more importantly, the readouts underneath it.
//
// The composite is an average of whichever readouts were present that day, with
// missing ones' weight spread across the rest. That is fine for a single day and
// wrong for a line: when a new readout starts arriving it joins the average and
// the score steps for a reason that has nothing to do with the person. On the
// first real record the index fell seven points over a stretch in which resting
// heart rate *improved*, purely because HRV appeared.
//
// So the composite is drawn only across the current run of readouts — the span
// in which it compares with itself — while each readout's own subscore is drawn
// across the whole window, because those are comparable throughout. The thing
// that actually got better stays visible even when the total can't be trusted.

const useIsDark = () => {
  const read = () =>
    typeof document !== 'undefined' &&
    document.documentElement.classList.contains('dark');
  const [dark, setDark] = useState(read);
  useEffect(() => {
    const el = document.documentElement;
    const obs = new MutationObserver(() => setDark(read()));
    obs.observe(el, { attributes: true, attributeFilter: ['class'] });
    return () => obs.disconnect();
  }, []);
  return dark;
};

// Gold is the composite — the one number the card is about. The readouts are
// deliberately quieter and share a neutral palette: they are context for the
// gold line, not four competing headlines.
const GOLD = '#F5A524';
const READOUT_COLORS = ['#0EA5E9', '#10B981', '#A78BFA', '#F472B6'];

// Everything is fetched once and filtered client-side, the way every other Data
// tab works — the shared range control remembers its choice across tabs, so
// refetching per range would put a network round-trip behind a button the user
// expects to be instant. The range itself is owned by the tab (MitoDataView),
// which is where the selector sits on every other tab too.
const HISTORY_DAYS = 1095;

const MitoHistoryChart = ({ inRange }) => {
  const dark = useIsDark();
  const [data, setData] = useState(null);
  const [failed, setFailed] = useState(false);

  useEffect(() => {
    let alive = true;
    apiClient
      .get(`/api/twin/mito/history?days=${HISTORY_DAYS}`)
      .then((res) => alive && setData(res.data))
      .catch(() => alive && setFailed(true));
    return () => { alive = false; };
  }, []);

  const series = useMemo(() => {
    if (!data?.points?.length) return [];
    return data.points.filter((p) => inRange(p.date)).map((p) => {
      const row = { date: p.date, coverage: p.coverage };
      // Only the current run gets a composite point. Recharts leaves a gap for
      // undefined, which is exactly the honest shape: no line where the number
      // isn't comparable.
      if (data.comparable_from && p.date >= data.comparable_from) {
        row.index = p.index;
      }
      (data.readouts || []).forEach((r) => {
        const v = p.subscores?.[r.metric_key];
        if (v != null) row[r.metric_key] = v;
      });
      return row;
    });
  }, [data, inRange]);

  if (failed) return null;
  if (!data) {
    return <div className="h-64 rounded-lg bg-gray-50 dark:bg-surface-dark animate-pulse" />;
  }

  const axis = dark ? '#9CA3AF' : '#6B7280';
  const grid = dark ? '#374151' : '#E5E7EB';

  return (
    <div>
      {!series.length ? (
        <p className="text-sm text-gray-700 dark:text-gray-200">
          No index history in this range. The index needs a few days of readouts
          before there's a line to draw.
        </p>
      ) : (
      <>
      <div className="h-56 w-full">
        <ResponsiveContainer width="100%" height="100%">
          <LineChart data={series} margin={{ top: 6, right: 6, bottom: 0, left: -18 }}>
            <CartesianGrid stroke={grid} strokeDasharray="3 3" vertical={false} />
            <XAxis
              dataKey="date"
              tickFormatter={formatDateTick}
              tick={{ fill: axis, fontSize: 12 }}
              minTickGap={28}
            />
            <YAxis
              domain={[0, 100]}
              ticks={[0, 25, 50, 75, 100]}
              tick={{ fill: axis, fontSize: 12 }}
            />
            <Tooltip
              contentStyle={{
                background: dark ? '#111827' : '#fff',
                border: `1px solid ${grid}`,
                borderRadius: 8,
                fontSize: 14,
              }}
              labelFormatter={formatDateTick}
              formatter={(value, name) => [Math.round(value), name]}
            />
            {(data.readouts || []).map((r, i) => (
              <Line
                key={r.metric_key}
                type="monotone"
                dataKey={r.metric_key}
                name={r.label}
                stroke={READOUT_COLORS[i % READOUT_COLORS.length]}
                strokeWidth={1.5}
                strokeOpacity={0.75}
                dot={false}
                connectNulls={false}
                isAnimationActive={false}
              />
            ))}
            <Line
              type="monotone"
              dataKey="index"
              name="Mito index"
              stroke={GOLD}
              strokeWidth={3}
              dot={false}
              connectNulls={false}
              isAnimationActive={false}
            />
          </LineChart>
        </ResponsiveContainer>
      </div>

      <div className="mt-2 flex flex-wrap items-center gap-x-4 gap-y-1">
        <span className="inline-flex items-center gap-1.5 text-sm font-medium text-gray-900 dark:text-white">
          <span className="inline-block w-4 h-1 rounded" style={{ background: GOLD }} />
          Mito index
        </span>
        {(data.readouts || []).map((r, i) => (
          <span key={r.metric_key}
                className="inline-flex items-center gap-1.5 text-sm text-gray-700 dark:text-gray-200">
            <span className="inline-block w-4 h-0.5 rounded"
                  style={{ background: READOUT_COLORS[i % READOUT_COLORS.length] }} />
            {r.label}
          </span>
        ))}
      </div>

      {/* Why the gold line starts where it does. Said plainly, next to the
          chart, rather than left for someone to infer from a gap. */}
      {data.note && (
        <p className="mt-2 text-sm text-gray-700 dark:text-gray-200 leading-relaxed">
          {data.note}
        </p>
      )}
      </>
      )}
    </div>
  );
};

export default MitoHistoryChart;
